// The seven unit choices, as a controlled group of selects.
//
// Presentation only: no fetching, no context, no persistence. The owner passes
// the current UnitSettings in and gets the whole updated object back on any
// change, so the same component drives the Profile page and (a later phase) the
// Instruments popover without either copy of the settings drifting.
//
// The option lists come from the protocol's *_UNITS arrays rather than being
// restated here — a new spelling then shows up in the picker automatically, and
// the per-category label maps below fail to compile until it is named.

import { useId } from 'react';
import {
  DEPTH_UNITS,
  DISTANCE_UNITS,
  PRESSURE_UNITS,
  SHORT_DISTANCE_UNITS,
  SPEED_UNITS,
  TEMPERATURE_UNITS,
  VOLUME_UNITS,
  type UnitSettings,
} from '@neomaris/protocol';

interface UnitField {
  readonly key: keyof UnitSettings;
  readonly label: string;
  readonly options: readonly { readonly value: string; readonly label: string }[];
  readonly withValue: (settings: UnitSettings, raw: string) => UnitSettings;
}

// Generic so each category's label map is checked against that category's own
// spellings. The cast is confined here: a <select> hands back a string, and the
// only strings it can hand back are the option values built one line above.
function field<K extends keyof UnitSettings>(spec: {
  key: K;
  label: string;
  units: readonly UnitSettings[K][];
  optionLabels: Record<UnitSettings[K], string>;
}): UnitField {
  return {
    key: spec.key,
    label: spec.label,
    options: spec.units.map((unit) => ({ value: unit, label: spec.optionLabels[unit] })),
    withValue: (settings, raw) => ({ ...settings, [spec.key]: raw as UnitSettings[K] }),
  };
}

const FIELDS: readonly UnitField[] = [
  field({
    key: 'distance',
    label: 'Distance',
    units: DISTANCE_UNITS,
    optionLabels: { nm: 'Nautical miles', mi: 'Miles', km: 'Kilometers' },
  }),
  field({
    key: 'shortDistance',
    label: 'Short distance',
    units: SHORT_DISTANCE_UNITS,
    optionLabels: { m: 'Meters', ft: 'Feet' },
  }),
  field({
    // One setting, because the helm reads boat speed and wind speed together.
    key: 'speed',
    label: 'Speed (boat & wind)',
    units: SPEED_UNITS,
    optionLabels: { kn: 'Knots', mph: 'MPH', kmh: 'km/h' },
  }),
  field({
    key: 'depth',
    label: 'Depth',
    units: DEPTH_UNITS,
    optionLabels: { m: 'Meters', ft: 'Feet', ftm: 'Fathoms' },
  }),
  field({
    key: 'temperature',
    label: 'Temperature',
    units: TEMPERATURE_UNITS,
    optionLabels: { C: '°C', F: '°F' },
  }),
  field({
    key: 'pressure',
    label: 'Pressure',
    units: PRESSURE_UNITS,
    optionLabels: { hPa: 'hPa', inHg: 'inHg' },
  }),
  field({
    key: 'volume',
    label: 'Volume',
    units: VOLUME_UNITS,
    optionLabels: { L: 'Liters', gal: 'US gallons' },
  }),
];

export function UnitSettingsFields({
  value,
  disabled = false,
  onChange,
}: {
  value: UnitSettings;
  disabled?: boolean;
  onChange: (next: UnitSettings) => void;
}) {
  // Two of these can be mounted at once (page plus popover), so the label/select
  // pairing has to be scoped per instance rather than to a fixed id.
  const idPrefix = useId();

  return (
    <div className="units-fields">
      {FIELDS.map((f) => (
        <div className="units-field" key={f.key}>
          <label htmlFor={`${idPrefix}-${f.key}`}>{f.label}</label>
          <select
            id={`${idPrefix}-${f.key}`}
            className="units-select"
            value={value[f.key]}
            disabled={disabled}
            onChange={(e) => onChange(f.withValue(value, e.target.value))}
            data-testid={`units-select-${f.key}`}
          >
            {f.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
}
